import * as APIRoutes from '@mm/api-routes';
import AuthenticatedController from './AuthenticatedController';
import {
  Plan,
  PlansMapping,
  PlansPrices,
  SubscriptionResponse,
  PlansAllotments,
  UserSeatsMapping,
  EnhancedPlan,
} from '../interfaces/subscription';
import { DEFAULT_HEADERS } from './Controller';
import {
  CreateProSubscriptionPayload,
  CreateTeamSubscriptionPayload,
  DowngradeOrCancelPayload,
  UpgradePayload,
  AttachPhoneNumberPayload,
} from '../actions/SubscriptionCRUDActions';
import {
  isBilling2023Plan,
  SubscriptionModel,
} from '../model/SubscriptionModel';

export type PlanResponse = {
  plan: Plan;
};

export type PlansResponse = {
  plans: Plan[];
  plansMapping: PlansMapping;
  plansPrices: PlansPrices;
  specialDeal: boolean;
  offerTeam: boolean;
  plansAllotments: PlansAllotments;
  annualPlansAllotments: PlansAllotments;
  plansUserSeats: UserSeatsMapping;
};

export type CouponObject = {
  code?: string;
  discount: { value: number; type: string } | null;
};

type CouponResponse = {
  coupon: CouponObject;

  // All the rest really don't serve a purpose
  discount: any;
  error_code: any;
  errors: string[];
  success: boolean;
};

type PromocodeResponse = {
  id: number;
  promoCodeId: number;
  createdAt: string;
  updatedAt: string;
  redeemedObjectId: number;
  redeemedObjectType: 'FacebookPage' | string;
};

type CreateSubscriptionResponse = {
  newSubscription: SubscriptionModel;
  oldSubscription?: SubscriptionModel;
};

type PostPayload = CreateProSubscriptionPayload | CreateTeamSubscriptionPayload;

export default class SubscriptionsController extends AuthenticatedController {
  static postSubscription(
    payload: PostPayload,
    plan: EnhancedPlan,
  ): Promise<CreateSubscriptionResponse | SubscriptionModel> {
    const route = isBilling2023Plan(plan)
      ? APIRoutes.SUBSCRIPTION_V2
      : APIRoutes.SUBSCRIPTION;

    return super.post(route, payload);
  }

  static updateSubscription(
    subscriptionId: number,
    payload: Pick<SubscriptionModel, 'xrayLimit'>,
  ): Promise<SubscriptionModel> {
    return super.put(APIRoutes.UPDATE_SUBSCRIPTION(subscriptionId), payload);
  }

  static attachSMSNumber(
    payload: AttachPhoneNumberPayload,
  ): Promise<AttachPhoneNumberPayload> {
    return super.post(APIRoutes.ATTACH_SMS_NUMBER, payload);
  }

  static submitSeatsNumber(payload: {
    subscriptionId: number;
    extraUserSeats: number;
  }): Promise<AttachPhoneNumberPayload> {
    return super.post(APIRoutes.SUBMIT_SEATS_COUNT(payload.subscriptionId), {
      extraUserSeats: payload.extraUserSeats,
    });
  }

  static getCreditsForFuturePayments(payload: {
    companyId: number;
    planCode: string;
    isUpgrade: boolean;
    subscriptionId: number;
  }) {
    const endPoint = payload.isUpgrade
      ? APIRoutes.UPGRADE_PLAN_CREDITS
      : APIRoutes.DOWNGRADE_PLAN_CREDITS;
    return super.get(endPoint(payload));
  }

  static sendOveragesEnabled(
    subscriptionId: number,
    overagesEnabled: boolean,
    facebookPageId: number,
    useV2Route?: boolean,
  ) {
    return super.post(
      APIRoutes.SUBSCRIPTION_OVERAGES_ENABLED(subscriptionId, useV2Route),
      {
        overages_enabled: overagesEnabled,
        facebook_page_id: facebookPageId,
      },
    );
  }
  static activate(subscriptionId: number) {
    return super.post(APIRoutes.SUBSCRIPTION_ACTIVATE(subscriptionId));
  }
  static upgrade(
    subscriptionId: number,
    payload: UpgradePayload,
    plan: EnhancedPlan,
  ) {
    const useV2Route = isBilling2023Plan(plan);
    return super.post(
      APIRoutes.SUBSCRIPTION_UPGRADE(subscriptionId, useV2Route),
      payload,
    );
  }

  static upgradeAllotment(payload: { subscriptionId: number; level: number }) {
    const { subscriptionId, level } = payload;
    return super.post(APIRoutes.SUBSCRIPTION_UPDATE_ALLOTMENT(subscriptionId), {
      sendAllotmentLevel: level,
    });
  }

  static planInfo(planCode: string): Promise<PlanResponse> {
    return super.get(APIRoutes.PLAN_INFO, { plan_code: planCode });
  }

  static getPlans(companyId: number): Promise<PlansResponse> {
    return super.get(APIRoutes.PLANS, { company_id: companyId });
  }

  static getPlan(planId: number): Promise<Plan> {
    return super.get(APIRoutes.SHOW_BILLING_PLAN_V2(planId));
  }

  static getSubscription(subscriptionId: number): Promise<SubscriptionModel> {
    return super.get(APIRoutes.SHOW_SUBSCRIPTION_V2(subscriptionId));
  }

  static getPriceForNextLevel(payload: {
    subscriptionId: number;
    nextLevel: number;
  }): Promise<{
    price: number;
  }> {
    const { subscriptionId, nextLevel } = payload;
    return super.get(
      APIRoutes.PLANS_FOR_NEXT_LEVEL({ subscriptionId, nextLevel }),
    );
  }

  static getAccountData(
    companyId: number,
    signal?: AbortSignal,
  ): Promise<SubscriptionResponse> {
    return super.get(
      APIRoutes.SUBSCRIPTION_ACCOUNT(companyId),
      null,
      DEFAULT_HEADERS,
      signal,
    );
  }

  static getCouponData(
    coupon: string,
    planCode: string | null,
    companyId: number,
  ): Promise<CouponResponse> {
    return super.get(APIRoutes.SUBSCRIPTION_COUPON, {
      plan_code: planCode,
      coupon,
      company_id: companyId,
    });
  }

  static cancel(subscriptionId: number, payload: DowngradeOrCancelPayload) {
    return super.post(APIRoutes.SUBSCRIPTION_CANCEL(subscriptionId), payload);
  }

  static downgrade(
    subscriptionId: number,
    payload: DowngradeOrCancelPayload,
    isV2Route?: boolean,
    isCancel?: boolean,
    plan?: EnhancedPlan,
  ): Promise<CreateSubscriptionResponse> {
    const useV2Route =
      isV2Route !== undefined ? isV2Route : plan && isBilling2023Plan(plan);

    return super.post(
      APIRoutes.SUBSCRIPTION_DOWNGRADE(subscriptionId, useV2Route, isCancel),
      payload,
    );
  }

  static addExtraPagesToTeam(subscriptionId: number, pagesCount: number) {
    return super.post(APIRoutes.SUBSCRIPTION_ADD_EXTRA_PAGES(subscriptionId), {
      pages_count: pagesCount,
    });
  }

  static swapPages(
    subscriptionId: number,
    facebookPageId: number,
    newPageId: number,
  ) {
    return super.post(APIRoutes.SUBSCRIPTION_SWAP_PAGES(subscriptionId), {
      facebook_page_id: facebookPageId,
      new_facebook_page_id: newPageId,
    });
  }

  static getCurrentSubscription(
    facebookPageId: number,
    bySubscription: boolean,
  ) {
    return bySubscription
      ? super.get(APIRoutes.EXTENDED_SUBSCRIPTION(facebookPageId))
      : super.get(APIRoutes.CURRENT_SUBSCRIPTION(facebookPageId));
  }

  static getCurrentSubscriptionCancels(
    subscriptionId: number,
  ): Promise<{ cancels: string }> {
    return super.get(APIRoutes.SUBSCRIPTION_CANCELS(subscriptionId));
  }

  static reedemPromocode(
    code: string,
    fbPageId: number,
  ): Promise<PromocodeResponse> {
    return super.post(APIRoutes.REEDEM_PROMOCODE(code, fbPageId));
  }
}
